import { useGetTradingLimitsRows } from '../utils'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { useSelector } from 'react-redux'
import React from 'react'
import { Groupbox } from 'view/components/panels'
import styled from '@emotion/styled'
import { Currency, TradingLimitType, User } from 'api'
import { SelectboxField } from 'view/components/options'
import { createGetTradingLimitCurrenciesUserSelector } from 'selectors/userSelectors'
import { identityPropsSelector, useParametricSelector } from 'utils'
import { Locale, Translation } from 'locale'
import { TableCell, TableHeader, TableRow, TableRows } from 'view/shared/servicingDialogs/servicingDialogStyles'
import { NumericInputField } from 'view/components/inputs'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { formatHeaderUnits } from './TradingLimitsGridModel'

const Wrapper = styled(Groupbox)`
  height: 470px;
`

export interface TradingLimitsPanelProps {
  user: User
  onUpdateUser: (user: Partial<User>) => void
  readOnly: boolean
}

const getTradingLimitCurrenciesUserSelector = createGetTradingLimitCurrenciesUserSelector(identityPropsSelector)

export const TradingLimitsPanel: React.FunctionComponent<TradingLimitsPanelProps> = props => {
  const userPreference = useSelector(userPreferenceSelector)
  const tradingLimitsUserUtilisation = useParametricSelector(getTradingLimitCurrenciesUserSelector, props.user.id)

  const tradingLimits = useGetTradingLimitsRows(props.user, tradingLimitsUserUtilisation)

  const handleCellValueUpdated = (currency: Currency, newValue: number) => {
    const limitsByCurrency = {
      ...props.user.tradingControlLimit.limitsByCurrency,
      [currency]: { limit: newValue }
    }
    if (newValue === 0) {
      delete limitsByCurrency[currency]
    }
    const newObj = {
      tradingControlLimit: {
        tradingLimitType: props.user.tradingControlLimit.tradingLimitType,
        limitsByCurrency
      }
    }
    props.onUpdateUser(newObj)
  }

  return (
    <div style={{ minWidth: '610px', height: '410px' }}>
      <SelectboxField
        label={Locale.translate(Translation.TC_TRADING_LIMIT_TYPE)}
        optionKeys={Object.values(TradingLimitType)}
        value={props.user.tradingControlLimit.tradingLimitType}
        onUpdate={val => {
          props.onUpdateUser({
            tradingControlLimit: { tradingLimitType: val, limitsByCurrency: props.user.tradingControlLimit.limitsByCurrency }
          })
        }}
        readOnly={props.readOnly}
      />
      <Wrapper>
        <TableHeader>
          <TableCell flex={1} left={true}>
            {Locale.translate(Translation.TC_CURRENCY)}
          </TableCell>
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.TC_TRADING_LIMIT)} {formatHeaderUnits(userPreference.amountFormat)}
          </TableCell>
          {props.user.tradingControlLimit.tradingLimitType === TradingLimitType.GROSS && (
            <>
              <TableCell flex={2} left={true}>
                {Locale.translate(Translation.TC_GROSS_UTILISATION)} {formatHeaderUnits(userPreference.amountFormat)}
              </TableCell>
              <TableCell flex={2} left={true}>
                {Locale.translate(Translation.TC_GROSS_AVAILABLE)} {formatHeaderUnits(userPreference.amountFormat)}
              </TableCell>
            </>
          )}
          {props.user.tradingControlLimit.tradingLimitType === TradingLimitType.NET && (
            <>
              <TableCell flex={2} left={true}>
                {Locale.translate(Translation.TC_NET_UTILISATION)} {formatHeaderUnits(userPreference.amountFormat)}
              </TableCell>
              <TableCell flex={2} left={true}>
                {Locale.translate(Translation.TC_NET_AVAILABLE_SELL)} {formatHeaderUnits(userPreference.amountFormat)}
              </TableCell>
              <TableCell flex={2} left={true}>
                {Locale.translate(Translation.TC_NET_AVAILABLE_BUY)} {formatHeaderUnits(userPreference.amountFormat)}
              </TableCell>
            </>
          )}
        </TableHeader>
        <TableRows>
          {tradingLimits.map(tradingLimit => {
            return (
              <TableRow key={tradingLimit.currency}>
                <TableCell flex={1} left={true}>
                  {tradingLimit.currency}
                </TableCell>
                <TableCell flex={2} left={true}>
                  <NumericInputField
                    label={''}
                    fieldName={Translation.TC_TRADING_LIMIT}
                    formatOptions={getNotionalFormatOptions(userPreference.amountFormat)}
                    value={tradingLimit.limit}
                    readOnly={props.readOnly}
                    onUpdate={val => {
                      if (val < 0) {
                        val = val * -1
                      }
                      handleCellValueUpdated(tradingLimit.currency, val)
                    }}
                    dataTestId={'TradingLimitsPanel-LimitInput-' + tradingLimit.currency}
                  />
                </TableCell>
                {props.user.tradingControlLimit.tradingLimitType === TradingLimitType.GROSS && (
                  <>
                    <TableCell flex={2} left={true}>
                      <span>
                        {Locale.formatNumber(tradingLimit.utilisationGross, getNotionalFormatOptions(userPreference.amountFormat))}
                      </span>
                    </TableCell>
                    <TableCell flex={2} left={true}>
                      <span>{Locale.formatNumber(tradingLimit.availableGross, getNotionalFormatOptions(userPreference.amountFormat))}</span>
                    </TableCell>
                  </>
                )}
                {props.user.tradingControlLimit.tradingLimitType === TradingLimitType.NET && (
                  <>
                    <TableCell flex={2} left={true}>
                      <span>{Locale.formatNumber(tradingLimit.utilisationNet, getNotionalFormatOptions(userPreference.amountFormat))}</span>
                    </TableCell>
                    <TableCell flex={2} left={true}>
                      {Locale.formatNumber(tradingLimit.availableNetSell, getNotionalFormatOptions(userPreference.amountFormat))}
                    </TableCell>
                    <TableCell flex={2} left={true}>
                      {Locale.formatNumber(tradingLimit.availableNetBuy, getNotionalFormatOptions(userPreference.amountFormat))}
                    </TableCell>
                  </>
                )}
              </TableRow>
            )
          })}
        </TableRows>
      </Wrapper>
    </div>
  )
}
